import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DialogShell } from "@/shared/ui/DialogShell";
import type { ReportGenUser } from "../interfaces/User";
import type {
  ExternalUserMapping,
  VendorDirectoryUser,
} from "../interfaces/ExternalUserMapping";
import type { VendorDirectoryEntry } from "@/features/bodycamMappings/api/bodycamMappingsApi";

interface Props {
  open: boolean;
  user: ReportGenUser | null;
  /** Connected bodycam vendor, used only to label fields and copy. */
  vendorLabel: string | null;
  /** The station's vendor roster, or why there isn't one. */
  directory: VendorDirectoryEntry | undefined;
  isLoadingDirectory: boolean;
  existingMappings: ExternalUserMapping[];
  /** Resolves the KARDA user behind a conflicting mapping to a display label. */
  resolveUserLabel: (userId: string) => string;
  onClose: () => void;
  /**
   * `conflict` is the active mapping the chosen id already belongs to, when it
   * belongs to a different KARDA user — confirming then reassigns it.
   */
  onConfirm: (
    user: ReportGenUser,
    externalUserId: string,
    conflict: ExternalUserMapping | null,
  ) => Promise<void>;
}

const SUGGEST_THRESHOLD = 3;

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j += 1) prev[j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j += 1) prev[j] = curr[j];
  }
  return prev[b.length];
}

function officerLabel(v: VendorDirectoryUser): string {
  return v.name || v.email || v.id;
}

const MapVendorOfficerDialog: React.FC<Props> = ({
  open,
  user,
  vendorLabel,
  directory,
  isLoadingDirectory,
  existingMappings,
  resolveUserLabel,
  onClose,
  onConfirm,
}) => {
  // The single source of truth for what gets mapped: picking from the roster
  // writes into it, and it stays editable for officers the roster is missing.
  const [externalUserId, setExternalUserId] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset state when the dialog opens for a new user.
  React.useEffect(() => {
    if (open) {
      setExternalUserId("");
      setSearch("");
    }
  }, [open, user?.id]);

  const roster = directory?.status === "ready" ? directory.data : null;
  // Stable identity so the filter memo below doesn't rerun every render.
  const vendorUsers = useMemo(() => roster?.users ?? [], [roster]);
  const hasRoster = vendorUsers.length > 0;

  const mappedByExternalId = useMemo(() => {
    const map = new Map<string, ExternalUserMapping>();
    for (const m of existingMappings) {
      if (m.active) map.set(m.externalUserId, m);
    }
    return map;
  }, [existingMappings]);

  const fullName = user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim().toLowerCase()
    : "";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const enriched = vendorUsers.map((vendorUser) => {
      const name = (vendorUser.name ?? "").toLowerCase();
      const distance =
        name && fullName ? levenshtein(name, fullName) : Infinity;
      const badgeMatch =
        !!vendorUser.badge &&
        !!user?.badgeNumber &&
        vendorUser.badge.trim().toLowerCase() ===
          user.badgeNumber.trim().toLowerCase();
      return {
        vendorUser,
        // A badge match is a much stronger signal than a fuzzy name match.
        suggested: badgeMatch || distance <= SUGGEST_THRESHOLD,
        rank: badgeMatch ? -1 : distance,
      };
    });
    const matches = !q
      ? enriched
      : enriched.filter(({ vendorUser }) => {
          return (
            vendorUser.id.toLowerCase().includes(q) ||
            vendorUser.name?.toLowerCase().includes(q) ||
            vendorUser.badge?.toLowerCase().includes(q) ||
            vendorUser.email?.toLowerCase().includes(q)
          );
        });
    return matches.sort((a, b) => {
      if (a.suggested !== b.suggested) return a.suggested ? -1 : 1;
      if (a.rank !== b.rank) return a.rank - b.rank;
      return officerLabel(a.vendorUser).localeCompare(
        officerLabel(b.vendorUser),
      );
    });
  }, [vendorUsers, search, fullName, user?.badgeNumber]);

  // Vendor ids are matched byte-exact — never normalize case or whitespace.
  // Trimming is only ever used to reject an empty field.
  const hasId = externalUserId.trim().length > 0;
  const existing = hasId ? mappedByExternalId.get(externalUserId) : undefined;
  const alreadyMappedToThisUser = !!existing && existing.userId === user?.id;
  const conflict = existing && existing.userId !== user?.id ? existing : null;
  const selectedOfficer = hasId
    ? vendorUsers.find((v) => v.id === externalUserId)
    : undefined;

  // Only an authoritative, untruncated directory licenses "this ID doesn't
  // exist" — otherwise absence just means "not in what we could see".
  const canAssertAbsence = roster?.source === "directory" && roster.complete;
  const unknownId = hasId && hasRoster && !selectedOfficer && canAssertAbsence;

  const canSubmit = hasId && !alreadyMappedToThisUser && !submitting;

  const handleConfirm = async () => {
    if (!user || !canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm(user, externalUserId, conflict);
    } finally {
      setSubmitting(false);
    }
  };

  const renderDirectoryNotice = () => {
    if (isLoadingDirectory || !directory) return null;
    if (directory.status === "unavailable") {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          {directory.message} Enter the officer's ID from the{" "}
          {vendorLabel ?? "vendor"} console instead.
        </Alert>
      );
    }
    if (!hasRoster) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          {vendorLabel ?? "The vendor"} returned no officers. Enter the
          officer's ID from the {vendorLabel ?? "vendor"} console instead.
        </Alert>
      );
    }
    if (roster && roster.source === "evidence_sample") {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          {vendorLabel ?? "This vendor"} publishes no officer directory, so
          this list is built from the last 30 days of evidence — officers
          without recent footage won't appear. Enter their ID manually if
          they're missing.
        </Alert>
      );
    }
    if (roster && !roster.complete) {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The vendor scan was cut short, so this list may be incomplete. Enter
          the officer's ID manually if they're missing.
        </Alert>
      );
    }
    return null;
  };

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      closeDisabled={submitting}
      size="sm"
      titleId="map-vendor-officer-dialog-title"
    >
      <DialogTitle id="map-vendor-officer-dialog-title" sx={{ pr: 6 }}>
        Map vendor officer
      </DialogTitle>
      <DialogContent dividers>
        {user && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              KARDA user
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
              {user.badgeNumber ? ` · Badge ${user.badgeNumber}` : null}
            </Typography>
          </Box>
        )}

        {renderDirectoryNotice()}

        {isLoadingDirectory && (
          <Box display="flex" alignItems="center" gap={1} py={2}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading {vendorLabel ?? "vendor"} officers…
            </Typography>
          </Box>
        )}

        {hasRoster && (
          <>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder={
                vendorLabel
                  ? `Search ${vendorLabel} officers by name, badge, email, or ID`
                  : "Search vendor officers"
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box mt={1} sx={{ maxHeight: 300, overflow: "auto" }}>
              {filtered.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  py={4}
                >
                  No officers match your search.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filtered.map(({ vendorUser, suggested }) => {
                    const mapping = mappedByExternalId.get(vendorUser.id);
                    const isSelected = vendorUser.id === externalUserId;
                    return (
                      <ListItemButton
                        key={vendorUser.id}
                        selected={isSelected}
                        onClick={() => setExternalUserId(vendorUser.id)}
                        sx={{ borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight={500}>
                                {officerLabel(vendorUser)}
                              </Typography>
                              {suggested && (
                                <Chip
                                  label="Suggested"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {mapping && mapping.userId !== user?.id && (
                                <Chip
                                  label={`Mapped to ${resolveUserLabel(mapping.userId)}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {mapping && mapping.userId === user?.id && (
                                <Chip
                                  label="Already mapped"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {vendorUser.badge
                                ? `Badge ${vendorUser.badge} · `
                                : null}
                              {vendorUser.email ? `${vendorUser.email} · ` : null}
                              ID: {vendorUser.id}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                or enter an ID manually
              </Typography>
            </Divider>
          </>
        )}

        <TextField
          autoFocus={!hasRoster}
          fullWidth
          size="small"
          label={vendorLabel ? `${vendorLabel} officer ID` : "Vendor officer ID"}
          value={externalUserId}
          onChange={(e) => setExternalUserId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          error={alreadyMappedToThisUser}
          helperText={
            alreadyMappedToThisUser
              ? "This ID is already mapped to this user."
              : selectedOfficer
                ? `Selected: ${officerLabel(selectedOfficer)}`
                : " "
          }
        />

        {unknownId && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {externalUserId} isn't in {vendorLabel ?? "the vendor"}'s officer
            directory. Check the ID — mapping it anyway won't attribute any
            evidence.
          </Alert>
        )}

        {conflict && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {externalUserId} is currently mapped to{" "}
            <strong>{resolveUserLabel(conflict.userId)}</strong>. Confirming
            reassigns it to this user.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={conflict ? "warning" : "primary"}
          onClick={handleConfirm}
          disabled={!canSubmit}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          {conflict ? "Reassign mapping" : "Confirm mapping"}
        </Button>
      </DialogActions>
    </DialogShell>
  );
};

export default MapVendorOfficerDialog;
